import React from 'react';
import { Box, Fade } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';

export const DIALOG_ZOOM_MS = 350;

/**
 * Transición Fade + zoom para Dialog. El wrapper ocupa el viewport y centra el contenido;
 * sin esto, el contenedor del Modal no tiene altura y el Paper queda pegado arriba.
 * `& > * { flexShrink: 0 }` evita que el Paper se aplaste como ítem flex al centrar.
 */
export const DialogZoomTransition = React.forwardRef(function DialogZoomTransition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  const { in: inProp, children, ...other } = props;

  return (
    <Fade ref={ref} in={inProp} timeout={DIALOG_ZOOM_MS} {...other}>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          pointerEvents: 'none',
          transform: inProp ? 'scale(1)' : 'scale(0.9)',
          transition: `transform ${DIALOG_ZOOM_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          transformOrigin: 'center center',
          '& > *': {
            flexShrink: 0,
          },
        }}
      >
        {children}
      </Box>
    </Fade>
  );
});

DialogZoomTransition.displayName = 'DialogZoomTransition';

/** slotProps del Dialog: el contenedor no intercepta clics (pasan al backdrop). */
export const dialogZoomSlotProps = {
  container: {
    sx: {
      pointerEvents: 'none',
    },
  },
} as const;

/** sx en el Dialog raíz: refuerza centrado del contenedor MUI. */
export const dialogZoomDialogSx = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiDialog-scrollPaper': {
    alignItems: 'center',
    justifyContent: 'center',
  },
} as const;

/** Combina tu `sx` del Paper con interacción y anti-encogido en cadenas flex. */
export function dialogZoomPaperSx<T extends Record<string, unknown>>(
  sx: T,
): T & { pointerEvents: 'auto' } {
  return {
    flexShrink: 0,
    boxSizing: 'border-box',
    ...sx,
    pointerEvents: 'auto' as const,
  };
}
